import { Alert, Box, Card, GridItem, Heading, HStack, SimpleGrid, Spinner, Text } from '@chakra-ui/react';
import { BellAlertIcon } from '@heroicons/react/24/outline';
import { useFocusedBranchContext } from '@packages/core/branch/focused-branch-context';
import { useFocusedCompanyContext } from '@packages/core/company/focused-company-context';
import { formatCurrency } from '@packages/core/utils/currency-formatter';
import { useTranslate } from '@tolgee/react';
import { DateRange } from '../../common/reservation-analytic-service';
import useGetTopServicesAppointmentReport from '../logic/use-get-top-services-appointment-report';

export default function TopServicesAppointmentReportCard(props: { dateRange: DateRange }) {
  const branch = useFocusedBranchContext();
  const { t } = useTranslate();
  const company = useFocusedCompanyContext();

  const { data, loading, error } = useGetTopServicesAppointmentReport(
    branch.branch?.id ?? '',
    props.dateRange.startDate,
    props.dateRange.endDate,
  );

  if (loading) {
    return (
      <Card.Root>
        <Card.Header>
          <Card.Title>
            <Heading fontWeight={'semibold'} fontSize="xl" color="gray.900">
              {t('appointment.report.topServices', 'Top Services')}
            </Heading>
          </Card.Title>
        </Card.Header>
        <Card.Body p={4} display="flex" justifyContent="center" alignItems="center">
          <Spinner size="xl" />
        </Card.Body>
      </Card.Root>
    );
  }

  if (error) {
    return (
      <Alert.Root status="error" borderRadius="md">
        <BellAlertIcon className="w-4 h-4" />
        <Box>
          <Alert.Title>Error loading top services!</Alert.Title>
          <Alert.Description>{error.message}</Alert.Description>
        </Box>
      </Alert.Root>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Alert.Root status="info" borderRadius="md">
        <BellAlertIcon className="w-4 h-4" />
        <Alert.Title>No top services data available</Alert.Title>
      </Alert.Root>
    );
  }

  return (
    <Card.Root>
      <Card.Header>
        <Card.Title>
          <Heading fontWeight={'semibold'} fontSize="xl" color="gray.900">
            {t('appointment.report.topServices', 'Top Services')}
          </Heading>
        </Card.Title>
      </Card.Header>
      <Card.Body p={4}>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={2}>
          {data.map((service, index) => (
            <GridItem key={index}>
              <HStack p={0} border={'solid 1px'} borderColor={'gray.300'} rounded={'md'}>
                <Text color={'gray.400'} fontWeight={'semibold'} fontSize={'2xl'} px={6}>
                  #{index + 1}
                </Text>
                <Box py={4}>
                  <Text fontSize={'sm'} fontWeight="bold">
                    {service.serviceName}
                  </Text>
                  <Text fontSize={'xs'}>
                    {t('appointment.report.topServices.totalSold', 'Total Sold: {count}', { count: service.totalSold })}
                  </Text>
                  <Text fontSize={'xs'}>
                    {t('appointment.report.topServices.totalRevenue', 'Total Revenue: {revenue}', {
                      revenue: formatCurrency(service.totalRevenue, company.settings.currency),
                    })}
                  </Text>
                </Box>
              </HStack>
            </GridItem>
          ))}
        </SimpleGrid>
      </Card.Body>
    </Card.Root>
  );
}
